/**
 * useDeferredAutoFocus — a drop-in replacement for the `autoFocus` attribute on
 * an input that lives inside a Radix Dialog/Sheet.
 *
 * Why this exists
 * ───────────────
 * Radix Dialog/Sheet open animations run for `RADIX_OPEN_ANIMATION_MS` (the
 * `duration-200` utility on `ui/sheet.tsx` / `ui/dialog.tsx`). Focusing a text
 * input DURING that slide/zoom makes iOS Safari raise the on-screen keyboard
 * mid-animation: the visual viewport resizes under the still-moving panel, so
 * the panel jumps — or, for a full-height sheet, flashes a dark, empty frame —
 * before it settles. Native `autoFocus` (and Radix's own open-autofocus) fire
 * on mount, i.e. exactly when the entrance begins, so they trigger this race
 * every time.
 *
 * Deferring focus until just after the animation lets the panel land first,
 * then the keyboard rises cleanly into the already-correct layout.
 *
 * Usage — correct usage is the whole return value
 * ───────────────────────────────────────────────
 * The hook returns BOTH halves of the two-step contract so a caller can't apply
 * one and forget the other:
 *   - `ref`            — spread onto the input; focus lands here post-animation.
 *   - `onOpenAutoFocus`— spread onto the Dialog/Sheet Content; prevents Radix's
 *                        own t=0 autofocus, which would otherwise raise the
 *                        keyboard immediately and defeat the deferral.
 *
 *   // co-located Content + input (e.g. a dialog):
 *   const { ref, onOpenAutoFocus } = useDeferredAutoFocus<HTMLInputElement>()
 *   <DialogContent onOpenAutoFocus={onOpenAutoFocus}>…<input ref={ref} />
 *
 *   // Content and input in different components (e.g. a sheet whose body is a
 *   // child): call the hook at the Content site, pass `ref` down as a prop, and
 *   // gate with `active` so the deferred focus re-arms on each open (the child
 *   // may not remount). See PairPicker.
 *
 * `active`
 * ────────
 * The focus timer (re-)arms whenever `active` transitions to true, so a host
 * that stays mounted across open/close (passing e.g. `active={isOpen}`) still
 * gets a fresh deferred focus per open. Defaults to true for the common case of
 * a Content that unmounts on close (Radix's default) — there the per-open mount
 * already re-runs the effect.
 */

import { useEffect, useRef } from 'react'

import {
  ANIMATION_SETTLE_SLACK_MS,
  RADIX_OPEN_ANIMATION_MS,
  prefersReducedMotion,
} from '@/components/ui/animation-tokens'

// Slack over the open animation, so focus lands after it has fully settled.
// Both terms come from the shared sheet/dialog tokens — no bare literals here.
const OPEN_ANIMATION_SETTLE_MS = RADIX_OPEN_ANIMATION_MS + ANIMATION_SETTLE_SLACK_MS

// Stable handler: stop Radix focusing the first focusable element at t=0 so the
// keyboard doesn't rise before the panel has settled (see `active`/usage above).
const preventOpenAutoFocus = (event: Event) => event.preventDefault()

export function useDeferredAutoFocus<T extends HTMLElement = HTMLInputElement>(
  active = true,
) {
  const ref = useRef<T>(null)
  useEffect(() => {
    if (!active) return
    // Under prefers-reduced-motion the global stylesheet collapses the open
    // animation to ~0ms (see frontend/src/index.css) — there's no slide to
    // race, so focus immediately rather than impose a gratuitous settle delay
    // (frontend/CLAUDE.md: all animations must respect prefers-reduced-motion).
    if (prefersReducedMotion()) {
      ref.current?.focus()
      return
    }
    const id = window.setTimeout(() => ref.current?.focus(), OPEN_ANIMATION_SETTLE_MS)
    return () => window.clearTimeout(id)
  }, [active])
  return { ref, onOpenAutoFocus: preventOpenAutoFocus }
}
